import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'

interface FundTerms {
  fund_id: number
  fund_name: string
  current_value: number | null
  notice_days: number
  redemption_frequency: string
  lockup: string | null
  lockup_months: number | null
}

interface RedemptionTerms {
  fund_name: string
  fund_id?: number
  redemption_frequency: string
  notice_days: number
  notice_type: string
  gate_pct?: number
  fund_gate_pct?: number
  holdback_pct?: number
  holdback_days?: number
  early_redemption_fee_pct?: number
  lockup_months?: number
  lockup_expiry?: string
  payment_days: number
  current_value?: number
}

interface TimelineEvent {
  date: string
  type: string
  amount_pct: number
  cumulative_pct: number
  amount?: number
  notes: string
}

interface RedemptionResult {
  fund_name: string
  timeline: TimelineEvent[]
  total_redeemed_pct: number
  total_fees_pct: number
  first_notice_deadline?: string
  first_redemption_date?: string
  full_redemption_date?: string
  summary: string
}

const defaultTerms: RedemptionTerms = {
  fund_name: '',
  redemption_frequency: 'Monthly',
  notice_days: 30,
  notice_type: 'calendar',
  payment_days: 30,
  current_value: 1000000,
}

const typeColors: Record<string, string> = {
  notice_deadline: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  redemption: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  payment: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  holdback_release: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
}

export default function RedemptionCalculator() {
  const [terms, setTerms] = useState<RedemptionTerms>(defaultTerms)
  const [result, setResult] = useState<RedemptionResult | null>(null)
  const [activeView, setActiveView] = useState<'calculator' | 'portfolio'>('calculator')

  const { data: fundTermsList } = useQuery({
    queryKey: ['redemption-fund-terms'],
    queryFn: () => api.get('/redemption/fund-terms').then(r => r.data) as Promise<FundTerms[]>,
  })

  const calculateMutation = useMutation({
    mutationFn: (payload: RedemptionTerms) => api.post('/redemption/calculate', payload).then(r => r.data),
    onSuccess: (data) => setResult(data),
  })

  const { data: portfolioSummary, isLoading: portfolioLoading } = useQuery({
    queryKey: ['redemption-portfolio'],
    queryFn: () => api.get('/redemption/portfolio-summary').then(r => r.data),
    enabled: activeView === 'portfolio',
  })

  const updateField = <K extends keyof RedemptionTerms>(field: K, value: RedemptionTerms[K]) => {
    setTerms(prev => ({ ...prev, [field]: value }))
  }

  const handleFundSelect = (fundId: string) => {
    if (!fundId || !fundTermsList) {
      setTerms(defaultTerms)
      setResult(null)
      return
    }
    const fund = fundTermsList.find((f: FundTerms) => f.fund_id === Number(fundId))
    if (fund) {
      setTerms({
        ...defaultTerms,
        fund_name: fund.fund_name,
        fund_id: fund.fund_id,
        current_value: fund.current_value || 1000000,
        notice_days: fund.notice_days || 30,
        redemption_frequency: fund.redemption_frequency || 'Monthly',
        lockup_months: fund.lockup_months || undefined,
      })
      setResult(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Redemption Calculator</h1>
        <p className="text-muted-foreground">Precise liquidity timeline with gates, holdbacks, fees, and notice periods</p>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        {(['calculator', 'portfolio'] as const).map(view => (
          <button
            key={view}
            onClick={() => setActiveView(view)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeView === view ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
            }`}
          >
            {view === 'calculator' ? 'Single Fund Calculator' : 'Portfolio Summary'}
          </button>
        ))}
      </div>

      {/* Calculator View */}
      {activeView === 'calculator' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold">Redemption Terms</h3>

            {/* Fund Selector Dropdown */}
            <div>
              <label className="text-xs text-muted-foreground">Select Fund</label>
              <select
                value={terms.fund_id ?? ''}
                onChange={e => handleFundSelect(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
              >
                <option value="">-- Select a portfolio fund --</option>
                {fundTermsList?.map((f: FundTerms) => (
                  <option key={f.fund_id} value={f.fund_id}>
                    {f.fund_name} ({formatCurrency(f.current_value)})
                  </option>
                ))}
              </select>
            </div>

            {/* Fund Name (editable, pre-filled from dropdown) */}
            <div>
              <label className="text-xs text-muted-foreground">Fund Name</label>
              <input
                type="text" value={terms.fund_name}
                onChange={e => updateField('fund_name', e.target.value)}
                placeholder="Or type a custom fund name"
                className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Frequency</label>
                <select value={terms.redemption_frequency} onChange={e => updateField('redemption_frequency', e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm">
                  <option>Monthly</option>
                  <option>Quarterly</option>
                  <option>Weekly</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Notice Type</label>
                <select value={terms.notice_type} onChange={e => updateField('notice_type', e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm">
                  <option value="calendar">Calendar Days</option>
                  <option value="business">Business Days</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Notice Days</label>
                <input type="number" value={terms.notice_days} onChange={e => updateField('notice_days', Number(e.target.value))}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Payment Days</label>
                <input type="number" value={terms.payment_days} onChange={e => updateField('payment_days', Number(e.target.value))}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Investor Gate %</label>
                <input type="number" value={terms.gate_pct ?? ''} onChange={e => updateField('gate_pct', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="100 = no gate"
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Holdback %</label>
                <input type="number" value={terms.holdback_pct ?? ''} onChange={e => updateField('holdback_pct', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Holdback Days</label>
                <input type="number" value={terms.holdback_days ?? ''} onChange={e => updateField('holdback_days', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Early Exit Fee %</label>
                <input type="number" value={terms.early_redemption_fee_pct ?? ''} onChange={e => updateField('early_redemption_fee_pct', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Lock-up (months)</label>
                <input type="number" value={terms.lockup_months ?? ''} onChange={e => updateField('lockup_months', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Current Value ($)</label>
                <input type="number" value={terms.current_value ?? ''} onChange={e => updateField('current_value', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" />
              </div>
            </div>

            <button
              onClick={() => calculateMutation.mutate(terms)}
              disabled={!terms.fund_name || calculateMutation.isPending}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
            >
              {calculateMutation.isPending ? 'Calculating...' : 'Calculate Timeline'}
            </button>

            {/* Pre-filled info */}
            {terms.fund_id && (
              <div className="text-[11px] text-muted-foreground bg-muted rounded p-2">
                Terms auto-filled from portfolio data. Adjust as needed before calculating.
              </div>
            )}
          </div>

          {/* Results */}
          <div className="lg:col-span-2 space-y-4">
            {result && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">Notice Deadline</div>
                    <div className="text-sm font-semibold">{formatDate(result.first_notice_deadline)}</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">First Redemption</div>
                    <div className="text-sm font-semibold">{formatDate(result.first_redemption_date)}</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">Full Redemption</div>
                    <div className="text-sm font-semibold">{formatDate(result.full_redemption_date)}</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">Total Fees</div>
                    <div className="text-sm font-semibold">{result.total_fees_pct > 0 ? `${result.total_fees_pct.toFixed(1)}%` : 'None'}</div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-muted rounded-lg p-3 text-sm">{result.summary}</div>

                {/* Timeline */}
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-semibold">Redemption Timeline</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Event</th>
                          <th className="text-right">Amount %</th>
                          <th className="text-right">Cumulative %</th>
                          <th className="text-right">Amount ($)</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.timeline.map((event, i) => (
                          <tr key={i}>
                            <td className="font-mono text-sm">{formatDate(event.date)}</td>
                            <td>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[event.type] || 'bg-muted'}`}>
                                {event.type.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="text-right font-mono">{event.amount_pct > 0 ? `${event.amount_pct.toFixed(1)}%` : '-'}</td>
                            <td className="text-right font-mono">{event.cumulative_pct > 0 ? `${event.cumulative_pct.toFixed(1)}%` : '-'}</td>
                            <td className="text-right font-mono">{event.amount ? formatCurrency(event.amount) : '-'}</td>
                            <td className="text-sm text-muted-foreground">{event.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {!result && (
              <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
                Select a fund from the dropdown or enter custom terms, then click Calculate
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portfolio Summary View */}
      {activeView === 'portfolio' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold">Portfolio Redemption Summary</h3>
          </div>
          {portfolioLoading ? (
            <div className="flex justify-center p-8"><span className="spinner" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th className="text-right">Current Value</th>
                    <th>Next Notice Deadline</th>
                    <th>Next Redemption</th>
                    <th>Full Redemption</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioSummary?.map((row: any) => (
                    <tr key={row.fund_id} className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        setActiveView('calculator')
                        handleFundSelect(String(row.fund_id))
                      }}
                    >
                      <td className="font-medium">{row.fund_name}</td>
                      <td className="text-right font-mono">{formatCurrency(row.current_value)}</td>
                      <td className="font-mono text-sm">{formatDate(row.next_notice_deadline)}</td>
                      <td className="font-mono text-sm">{formatDate(row.next_redemption_date)}</td>
                      <td className="font-mono text-sm">{formatDate(row.full_redemption_date)}</td>
                      <td className="text-sm text-muted-foreground">{row.summary || row.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 text-[10px] text-muted-foreground border-t border-border">
                Click a fund row to open it in the calculator with pre-filled terms
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
